import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useWebContainer } from './WebContainerContext';
import { Loader2, Globe, AlertCircle, ArrowLeft } from 'lucide-react';

export function PreviewPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { status, serverUrl, runProject, error: wcError } = useWebContainer();
  const [error, setError] = useState(null);
  const [websiteData, setWebsiteData] = useState(null);

  useEffect(() => {
    const fetchWebsite = async () => {
      try {
        const response = await fetch(`/api/shared-website/${id}`);
        const data = await response.json();
        if (data.success) {
          setWebsiteData(data);
          await runProject(data.files);
        } else {
          setError('Website not found or expired.');
        }
      } catch (err) {
        console.error('Failed to load shared website:', err);
        setError('Failed to load shared website.');
      }
    };

    fetchWebsite();
  }, [id, runProject]);

  return (
    <div className="fixed inset-0 bg-white flex flex-col">
      {/* Mini Header */}
      <header className="h-14 px-6 border-b border-slate-100 flex items-center justify-between bg-white z-10">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => navigate('/')}
            className="p-2 hover:bg-slate-50 rounded-lg transition-all text-slate-400"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex flex-col">
            <h1 className="text-xs font-black text-slate-900 uppercase tracking-widest">
              Shared AI Website Preview
            </h1>
            <span className="text-[9px] font-bold text-slate-400 uppercase tracking-tighter">
              {websiteData?.prompt || 'Loading...'}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className={`px-3 py-1 rounded-full text-[8px] font-black uppercase tracking-widest flex items-center gap-1.5 ${
            status === 'running' ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'
          }`}>
            <div className={`w-1.5 h-1.5 rounded-full ${status === 'running' ? 'bg-emerald-500 animate-pulse' : 'bg-amber-500'}`} />
            {status}
          </div>
        </div>
      </header>

      {/* Main Preview Area */}
      <main className="flex-1 relative bg-slate-50">
        {status === 'running' && serverUrl ? (
          <iframe 
            src={serverUrl} 
            className="w-full h-full border-none"
            title="Shared Preview"
          />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center p-12 text-center">
            {error || wcError ? (
              <div className="space-y-6 max-w-sm">
                <div className="w-20 h-20 bg-rose-50 rounded-3xl flex items-center justify-center mx-auto">
                  <AlertCircle className="w-10 h-10 text-rose-500" />
                </div>
                <div className="space-y-2">
                  <h2 className="text-sm font-black text-slate-900 uppercase tracking-widest">Runtime Exception</h2>
                  <p className="text-[11px] font-bold text-slate-400 leading-relaxed">
                    {error || wcError}
                  </p>
                </div>
                <button 
                  onClick={() => window.location.reload()}
                  className="px-6 py-2 bg-slate-900 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:scale-105 transition-all"
                >
                  Retry Boot
                </button>
              </div>
            ) : (
              <div className="space-y-8">
                <div className="relative">
                  <div className="w-24 h-24 bg-indigo-50 rounded-[2rem] flex items-center justify-center">
                    <Globe className="w-10 h-10 text-indigo-300" />
                  </div>
                  <div className="absolute -top-2 -right-2 w-10 h-10 bg-white rounded-full shadow-xl flex items-center justify-center">
                    <Loader2 className="w-5 h-5 text-indigo-500 animate-spin" />
                  </div>
                </div>
                <div className="space-y-3">
                  <h3 className="text-sm font-black text-slate-900 uppercase tracking-widest animate-pulse">
                    Synthesizing Shared Environment
                  </h3>
                  <div className="flex flex-col gap-1.5">
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
                      Booting Virtual Node.js Runtime...
                    </p>
                    <div className="w-48 h-1 bg-slate-100 rounded-full mx-auto overflow-hidden">
                      <div className="h-full bg-indigo-500 w-1/3 animate-[loading_2s_infinite_ease-in-out]" />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes loading {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(200%); }
        }
      `}} />
    </div>
  );
}
